// use-feature-lockout.ts
// This file provides hooks for managing feature lockout state for a given case and feature.
// Integrates with Athena/Cerner context and React Query for state management.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAppState } from "@/contexts/app-state";
import { getLockout, postLockout, deleteLockout } from "@/services/lockout";
import { Lockout } from "@/types/lockout";

/**
 * useFeatureLockout Hook
 * Fetches the lockout state for a given case and feature, using the current user and department context.
 *
 * @param caseId - The unique identifier for the case
 * @param feature - The feature for which the lockout is being managed
 * @returns The query object containing data, status, and an isLockedOutForUser helper
 *
 * @example
 * const { data, isLoading, isLockedOutForUser } = useFeatureLockout("1234", "update-case-details");
 */
export const useFeatureLockout = (caseId: string, feature: string) => {
  const { loggedInUser, context } = useAppState();

  const query = useQuery<Lockout | "", Error>({
    queryKey: ["lockout", { user: loggedInUser, caseId, feature }],
    queryFn: () =>
      getLockout({
        user: loggedInUser,
        practiceId: context?.athena?.practiceId || "8363",
        caseId,
        feature,
        requestorId: loggedInUser,
      }),
  });

  const isLockedOutForUser = () =>
    (!query.data || query.data?.userId === loggedInUser) ? false : true;

  return {
    ...query,
    isLockedOutForUser,
  };
};

/**
 * useCreateLockout Hook
 * Provides a mutation for creating a lockout for a given case and feature.
 *
 * @param caseId - The unique identifier for the case
 * @param feature - The feature for which the lockout is being managed
 * @returns An object with mutation state and a createLockout function
 *
 * @example
 * const { createLockout, isPending } = useCreateLockout("1234", "update-case-details");
 * createLockout();
 */
export const useCreateLockout = (caseId: string, feature: string) => {
  const queryClient = useQueryClient();
  const { loggedInUser, context } = useAppState();
  const { isPending, isError, isSuccess, mutate, error } = useMutation({
    mutationFn: postLockout,
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["lockout", { user: loggedInUser, caseId }],
      });
    },
  });

  const createLockout = () => {
    mutate({
      user: loggedInUser,
      requestorId: loggedInUser,
      practiceId: context?.athena?.practiceId || "8363",
      caseId,
      feature
    });
  };

  return {
    isPending,
    isError,
    error,
    isSuccess,
    createLockout,
  };
};

/**
 * useRemoveLockout Hook
 * Provides a mutation for removing a lockout for a given case and feature.
 *
 * @param caseId - The unique identifier for the case
 * @param feature - The feature for which the lockout is being managed
 * @returns An object with mutation state and a removeLockout function
 *
 * @example
 * const { removeLockout, isPending } = useRemoveLockout("1234", "update-case-details");
 * removeLockout();
 */
export const useRemoveLockout = (caseId: string, feature: string) => {
  const queryClient = useQueryClient();
  const { loggedInUser, context } = useAppState();
  const { isPending, isError, isSuccess, mutate, error } = useMutation({
    mutationFn: deleteLockout,
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["lockout", { user: loggedInUser, caseId }],
      });
    },
  });

  const removeLockout = () => {
    mutate({
      user: loggedInUser,
      requestorId: loggedInUser,
      practiceId: context?.athena?.practiceId || "8363",
      caseId,
      feature
    });
  };

  return {
    isPending,
    isError,
    error,
    isSuccess,
    removeLockout,
  };
};

/**
 * useLockout Hook
 * Combines all lockout-related hooks into a single interface for a given case and feature.
 *
 * @param caseId - The unique identifier for the case
 * @param feature - The feature for which the lockout is being managed
 * @returns An object with get, post, and delete helpers
 *
 * @example
 * const { get, post, delete } = useLockout("1234", "feature");
 * const { data, isLoading } = get;
 * // or
 * const { get: { data, isLoading } } = useLockout("1234", "feature");
 */
export const useLockout = (caseId: string, feature: string) => {
  return {
    get: useFeatureLockout(caseId, feature),
    post: useCreateLockout(caseId, feature),
    delete: useRemoveLockout(caseId, feature),
  };
};
